import { SpaCareReturn } from '../components/SpaCareReturn';
import { useEffect } from 'react';
import { ArrowRightIcon, CalendarCheckIcon, PhoneIcon, SnowflakeIcon, WrenchIcon } from 'lucide-react';
import { business, images, routes } from '../data/site';
import { SiteLink } from '../components/SiteLink';
const TITLE = 'Spa Service Work Guarantee | Pegasus Spas';
const DESCRIPTION = 'Review the Pegasus Spas service-work guarantee, customer responsibilities, winterizing terms and after-hours service policy.';
const CANONICAL = 'https://www.pegasus-spas.com/Service-Work-Guarantee';
const summary = [{
  icon: CalendarCheckIcon,
  title: '30-day return-visit term',
  body: 'For the exact same problem within 30 days, no additional service-call fee applies. New parts and additional labor are still charged.'
}, {
  icon: WrenchIcon,
  title: 'OEM part coverage',
  body: "Replacement-part warranty varies by manufacturer. Labor for replacing warranty parts is billed at the standard hourly rate."
}, {
  icon: SnowflakeIcon,
  title: 'Winterizing term',
  body: 'Spas winterized and bagged by Pegasus are guaranteed for six months from the service date, subject to the conditions below.'
}];
const guaranteeTerms = ['We use genuine OEM-approved parts whenever possible and replace only what is necessary to correct the issue.', 'Parts that appear worn, damaged, or outside OEM voltage and amperage ratings are not replaced unless the customer specifically requests it.', 'If Pegasus must return to replace an additional part related to the same issue, and it is the exact same problem within 30 days, the customer is charged for the new part and additional labor, but no extra service-call fee applies.', 'If the return visit is for a different issue or occurs more than 30 days after the original service, the standard service-call rate applies along with required labor and parts.', "Replacement parts carry the OEM manufacturer's warranty, which varies by part. Labor to replace warranty parts is not covered by the manufacturer and is billed at Pegasus' standard hourly rate.", 'Repairs completed “in shop” must be returned to the shop for warranty work.', 'The service guarantee applies only to work performed by Pegasus Spas technicians and does not extend to third-party repairs or customer-installed parts.'];
const responsibilities = ['The spa must be full of water unless one of our service technicians directs otherwise. We cannot locate leaks or properly test systems without water. A service-call fee is charged if we arrive and cannot perform service because the spa is empty.', 'Clear, safe access to the spa must be available at the scheduled time.', 'Power must be available and turned on unless Pegasus specifically asks for it to be disconnected.', 'If something does not seem right after service, call as soon as possible within the 30-day window so we can address it. Requests after that window may be billed as a new visit.'];
const winterConditions = ['The spa must remain completely free of water throughout the winter.', 'Heavy snow loads must be removed from the cover and spa area to prevent damage.', 'Damage resulting from standing water, snow weight, refilling or power loss is not covered under this guarantee.'];
const relatedLinks = [{
  label: 'Service & Repair',
  href: routes.service
}, {
  label: 'Spa Care & FAQs',
  href: routes.spaCare
}, {
  label: 'Contact Us',
  href: routes.contact
}];
function upsertMeta(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
export function WorkGuaranteePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = TITLE;
    upsertMeta('description', DESCRIPTION);
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', CANONICAL);
  }, []);
  return <main data-id="element-2484" data-mp="3uslrcid2fzxn">
      <SpaCareReturn data-id="element-2485" data-mp="3us2a73v8pfko" />
      {/* Intro */}
      <section aria-labelledby="guarantee-h1" className="w-full bg-water" data-id="element-2486" data-mp="3ut4yd416h3ww">
        <div className="mx-auto max-w-container px-5 py-10 sm:px-8 lg:py-12" data-id="element-2487" data-mp="3742km79nzv7o">
          <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-12 lg:gap-12" data-id="element-2488" data-mp="324g8601nuizx">
            <div className="lg:col-span-8" data-id="element-2489" data-mp="3gskh4cawvg5i">
              <p className="text-[14px] font-bold uppercase text-pegasus-blue" data-id="element-2490" data-mp="3gtm1j7dthezw">
                Our Service Guarantee
              </p>
              <h1 id="guarantee-h1" className="mt-2 text-h2-mobile font-semibold text-ink lg:text-[40px] lg:leading-[48px]" data-id="element-2491" data-mp="3c09ev2sp7i9s">
                The Pegasus Service Work Guarantee
              </h1>
              <p className="mt-4 max-w-3xl text-body text-ink/80 sm:text-body-lg" data-id="element-2492" data-mp="yv4jf0kjhz5p">
                At Pegasus Spas, we take pride in the quality of our work and stand behind every
                service we perform. Our goal is to resolve the issue you called us for — and help
                you avoid future ones — with the same care and honesty we would expect in our own
                homes.
              </p>
            </div>

            <div className="lg:col-span-4" data-id="element-2493" data-mp="1hmougoompovd">
              <div className="aspect-[4/3] w-full overflow-hidden rounded-card" data-id="element-2494" data-mp="1mlib2pz7pvzr">
                <img src={images.pathService} alt="Spa technician inspecting plumbing inside an open hot tub equipment bay" className="h-full w-full object-cover object-center" loading="lazy" decoding="async" data-id="element-2495" data-mp="18l7k7mxwkhel" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* At a glance */}
      <section aria-labelledby="glance-heading" className="w-full border-y border-ink/10 bg-white" data-id="element-2496" data-mp="23uu9y9fmsqie">
        <div className="mx-auto max-w-container px-5 py-10 sm:px-8 lg:py-12" data-id="element-2497" data-mp="1ywj2248wcq2t">
          <h2 id="glance-heading" className="text-[15px] font-bold uppercase text-ink/60" data-id="element-2498" data-mp="1u3pxbgdfhhqq">
            At a glance
          </h2>
          <dl className="mt-6 grid grid-cols-1 gap-x-10 gap-y-7 lg:grid-cols-3" data-id="element-2499" data-mp="1p4vfohr75z87">
            {summary.map(item => <div key={item.title} className="border-t border-ink/15 pt-4" data-id="element-2500" data-mp="1l1qfilula9mq">
                <item.icon className="h-5 w-5 text-pine" aria-hidden="true" data-id="element-2501" data-mp="1g35eik7ijlx8" />
                <dt className="mt-2.5 text-[18px] font-semibold leading-7 text-ink" data-id="element-2502" data-mp="1b6opzowej5jm">
                  {item.title}
                </dt>
                <dd className="mt-1.5 text-[16px] leading-6 text-ink/80" data-id="element-2503" data-mp="168n7p66sva97">{item.body}</dd>
              </div>)}
          </dl>
        </div>
      </section>

      {/* Service work guarantee terms */}
      <section aria-labelledby="terms-heading" className="w-full bg-white" data-id="element-2504" data-mp="30iqutqi3q7zf">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-2505" data-mp="3912ick2ay98i">
          <h2 id="terms-heading" className="text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-2506" data-mp="3d24dopt3m157">
            Service Work Guarantee
          </h2>

          <ul className="mt-7 max-w-4xl space-y-4 border-t border-ink/15 pt-6" data-id="element-2507" data-mp="3msz8wjobob2s">
            {guaranteeTerms.map(term => <li key={term} className="text-[16.5px] leading-7 text-ink/85" data-id="element-2508" data-mp="2q3hv1hf6riq0">
                {term}
              </li>)}
          </ul>
        </div>
      </section>

      {/* Customer responsibilities */}
      <section aria-labelledby="responsibilities-heading" className="w-full border-y border-ink/10 bg-white" data-id="element-2509" data-mp="2z2d554lcpcyl">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-2510" data-mp="340elhhre6y6d">
          <h2 id="responsibilities-heading" className="text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-2511" data-mp="ecnmeghj170m">
            Customer Responsibilities
          </h2>

          <ul className="mt-7 max-w-4xl space-y-0 border-t border-ink/15" data-id="element-2512" data-mp="14guej3ois599">
            {responsibilities.map(item => <li key={item} className="border-b border-ink/10 py-4 text-[16.5px] leading-7 text-ink/85" data-id="element-2513" data-mp="10cwacekc849c">
                {item}
              </li>)}
          </ul>

          <SiteLink to={routes.service} className="mt-6 inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-bold text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-2514" data-mp="1o41tp3phleoe">
            What to send with a service request
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2515" data-mp="2qyzn3c7937y" />
          </SiteLink>
        </div>
      </section>

      {/* Winterizing guarantee */}
      <section aria-labelledby="winter-heading" className="w-full bg-water" data-id="element-2516" data-mp="3tyjsj6xgyfxh">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-2517" data-mp="29exxsscaedu4">
          <div className="flex items-start gap-3" data-id="element-2518" data-mp="2ee13mf03co35">
            <SnowflakeIcon className="mt-1 h-6 w-6 shrink-0 text-pegasus-blue" aria-hidden="true" data-id="element-2519" data-mp="2ediystzd51jm" />
            <h2 id="winter-heading" className="text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-2520" data-mp="24o973f8flkw1">
              Winterizing Guarantee
            </h2>
          </div>

          <p className="mt-5 max-w-4xl text-body font-semibold text-ink sm:text-body-lg" data-id="element-2521" data-mp="1vocfb4qqw5wr">
            Spas winterized and bagged by Pegasus Spas are guaranteed for six (6) months from the
            date of service.
          </p>

          <h3 className="mt-7 text-[18px] font-semibold leading-7 text-ink" data-id="element-2522" data-mp="1lq59ni1wpaa2">
            Conditions of this guarantee
          </h3>
          <ul className="mt-3 max-w-4xl space-y-2" data-id="element-2523" data-mp="3c088zr47ykgk">
            {winterConditions.map(condition => <li key={condition} className="text-[16.5px] leading-7 text-ink/85" data-id="element-2524" data-mp="3manqrz2laszs">
                {condition}
              </li>)}
          </ul>
        </div>
      </section>

      {/* After-hours */}
      <section aria-labelledby="after-hours-heading" className="w-full bg-white" data-id="element-2525" data-mp="3ccrj5h8v2g6f">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-2526" data-mp="3hbkkycesciqr">
          <h2 id="after-hours-heading" className="text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-2527" data-mp="3h9hx9z13v1p7">
            After-Hours Service
          </h2>
          <p className="mt-5 max-w-4xl text-body text-ink/85 sm:text-body-lg" data-id="element-2528" data-mp="du2pl4g79478">
            After-hours or emergency service requests may be accommodated at Pegasus' discretion and
            are subject to additional rates. We are not an on-call emergency provider, but we will do
            our best to assist whenever possible.
          </p>
          <p className="mt-4 max-w-4xl text-[16px] leading-6 text-ink/75" data-id="element-2529" data-mp="2omncf10dm0b7">
            If a spa stops working in freezing weather, review our{' '}
            <SiteLink to={`${routes.blog}#featured`} className="font-bold text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-2530" data-mp="2h1tdjwkex8v5">
              Winter Storm Tips
            </SiteLink>{' '}
            and contact us promptly.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section aria-labelledby="rates-heading" className="w-full bg-ink" data-id="element-2531" data-mp="1tebq2d86kwxz">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-2532" data-mp="1tbz1hh7x3oby">
          <div className="max-w-3xl" data-id="element-2533" data-mp="2840xgq6v9x6p">
            <h2 id="rates-heading" className="text-h2-mobile font-semibold text-white lg:text-h2-desktop" data-id="element-2534" data-mp="23c9a0oj0c7ks">
              Ask About Current Service Rates and Scheduling
            </h2>
            <p className="mt-4 text-body text-white/85 sm:text-body-lg" data-id="element-2535" data-mp="1am97uxfw58lm">
              Call or text with the spa details and service address, or submit a request online.
            </p>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3" data-id="element-2536" data-mp="24bzvz43j6h3s">
            <a href={business.phoneHref} className="inline-flex min-h-[48px] items-center gap-2 rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-2537" data-mp="2941akwo68jwz">
              <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-2538" data-mp="1heok84ed1mjx" />
              Call or text {business.phone}
            </a>
            <SiteLink to={routes.contact} className="inline-flex min-h-[48px] items-center rounded-btn border-2 border-white/70 px-5 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-white hover:text-ink" data-id="element-2539" data-mp="1qdjo39txmkk2">
              Request Service
            </SiteLink>
          </div>
        </div>
      </section>

      {/* Related links */}
      <section aria-label="Related pages" className="w-full border-t border-ink/10 bg-white" data-id="element-2540" data-mp="v3wsi6pivh8c">
        <div className="mx-auto max-w-container px-5 py-6 sm:px-8" data-id="element-2541" data-mp="q5d9of686lk7">
          <ul className="flex flex-col gap-x-8 gap-y-1 sm:flex-row sm:flex-wrap sm:items-center" data-id="element-2542" data-mp="q76f5r53qzqm">
            {relatedLinks.map(link => <li key={link.label} data-id="element-2543" data-mp="gftlctzqtzhy">
                <SiteLink to={link.href} className="inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-2544" data-mp="geqwerlze74o">
                  {link.label}
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2545" data-mp="3tn3sua2h2ptp" />
                </SiteLink>
              </li>)}
          </ul>
        </div>
      </section>
    </main>;
}